// QualityDashboard.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ScreenHeader from "../../components/ScreenHeader";
import {
  LogOut,
  FileText,
  Truck,
  Wrench,
  Layers,
  Package,
  ClipboardList,
  FileCheck,
  BarChart3,
  Undo2,
  AlertTriangle,
  XCircle,
  Database,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  ChevronDown,
  CheckCircle,
  Printer,
  Download,
  Send,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  Save,
  X
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function QualityDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pending-po");
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // ==================== STATE MANAGEMENT ====================

  // Pending PO State
  const [pendingPOs, setPendingPOs] = useState([
    { id: 1, poNumber: "PO-001", vendor: "ABC Industries", date: "2024-01-10", items: [{ name: "Steel Plate", qty: 100 }], status: "pending" },
    { id: 2, poNumber: "PO-002", vendor: "XYZ Corp", date: "2024-01-12", items: [{ name: "Aluminum Sheet", qty: 50 }], status: "pending" }
  ]);

  // Goods Received from Vendor State
  const [goodsReceived, setGoodsReceived] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receivedData, setReceivedData] = useState({
    poId: "",
    quantityAccepted: "",
    quantityRejected: "",
    quantityNC: {
      return: "",
      scrap: "",
      rework: { inhouse: "", outsource: "" }
    }
  });

  // Direct FG Checklist State
  const [directFGItems, setDirectFGItems] = useState([
    { id: 1, productName: "CNC Machined Part A", productCode: "CNC-001", checklist: [{ name: "Dimension Check", passed: true, remark: "" }, { name: "Surface Finish", passed: true, remark: "" }], status: "approved" },
    { id: 2, productName: "Hydraulic Valve Body", productCode: "HV-002", checklist: [{ name: "Pressure Test", passed: false, remark: "Leakage detected" }, { name: "Dimension Check", passed: true, remark: "" }], status: "rejected" }
  ]);
  const [showDirectFGModal, setShowDirectFGModal] = useState(false);
  const [editingDirectFG, setEditingDirectFG] = useState(null);
  const [directFGForm, setDirectFGForm] = useState({
    productName: "",
    productCode: "",
    checklist: [{ name: "", passed: false, remark: "" }],
    status: "pending"
  });

  // Sub Assembly Checklist State
  const [subAssemblyItems, setSubAssemblyItems] = useState([
    { id: 1, assemblyName: "Motor Assembly", assemblyCode: "MA-001", checklist: [{ name: "Winding Test", passed: true, remark: "" }, { name: "Insulation Test", passed: true, remark: "" }], status: "approved" },
    { id: 2, assemblyName: "Gearbox Assembly", assemblyCode: "GB-002", checklist: [{ name: "Gear Mesh Check", passed: true, remark: "" }, { name: "Lubrication Check", passed: false, remark: "Low oil level" }], status: "rejected" }
  ]);
  const [showSubAssemblyModal, setShowSubAssemblyModal] = useState(false);
  const [editingSubAssembly, setEditingSubAssembly] = useState(null);
  const [subAssemblyForm, setSubAssemblyForm] = useState({
    assemblyName: "",
    assemblyCode: "",
    checklist: [{ name: "", passed: false, remark: "" }],
    status: "pending"
  });

  // Inspection List State (combines Direct FG and Sub Assembly)
  const [inspections, setInspections] = useState([
    { id: 1, type: "direct_fg", itemName: "CNC Machined Part A", inspectionDate: "2024-01-15", inspector: "John Doe", result: "pass", remarks: "" },
    { id: 2, type: "sub_assembly", itemName: "Motor Assembly", inspectionDate: "2024-01-14", inspector: "Jane Smith", result: "fail", remarks: "Winding issue" }
  ]);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({
    type: "direct_fg",
    itemId: "",
    inspectionDate: "",
    inspector: "",
    result: "pass",
    remarks: ""
  });

  // Sales Return State
  const [salesReturns, setSalesReturns] = useState([
    { id: 1, sno: 1, customer: "ABC Corp", product: "Steel Bracket", productCode: "SB-001", quantity: 10, invoiceNo: "INV-001", dcNo: "DC-001", ewayNo: "EW-001", status: "pending", addToSchedule: false }
  ]);
  const [showSalesReturnModal, setShowSalesReturnModal] = useState(false);
  const [editingSalesReturn, setEditingSalesReturn] = useState(null);
  const [salesReturnForm, setSalesReturnForm] = useState({
    sno: "",
    customer: "",
    product: "",
    productCode: "",
    quantity: "",
    invoiceNo: "",
    dcNo: "",
    ewayNo: "",
    status: "pending",
    addToSchedule: false
  });

  // Inspection Report State
  const [inspectionReport, setInspectionReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    startDate: "",
    endDate: "",
    type: "all",
    result: "all"
  });

  // Search and Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // ==================== HELPER FUNCTIONS ====================

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "npd-dashboard") {
      navigate('/ndp-dashboard');
    }
  };

  // ==================== GOODS RECEIVED FUNCTIONS ====================

  const handleSelectPO = (po) => {
    setSelectedPO(po);
    setReceivedData({
      poId: po.id,
      quantityAccepted: "",
      quantityRejected: "",
      quantityNC: {
        return: "",
        scrap: "",
        rework: { inhouse: "", outsource: "" }
      }
    });
  };

  const handleReceivedDataChange = (field, value) => {
    setReceivedData(prev => ({ ...prev, [field]: value }));
  };

  const handleNCChange = (category, subCategory, value) => {
    if (subCategory) {
      setReceivedData(prev => ({
        ...prev,
        quantityNC: {
          ...prev.quantityNC,
          [category]: {
            ...prev.quantityNC[category],
            [subCategory]: value
          }
        }
      }));
    } else {
      setReceivedData(prev => ({
        ...prev,
        quantityNC: {
          ...prev.quantityNC,
          [category]: value
        }
      }));
    }
  };

  const handleSubmitGoodsReceived = () => {
    const newReceived = {
      id: goodsReceived.length + 1,
      poNumber: selectedPO.poNumber,
      vendor: selectedPO.vendor,
      date: new Date().toLocaleDateString(),
      ...receivedData
    };
    setGoodsReceived([...goodsReceived, newReceived]);
    setPendingPOs(pendingPOs.filter(po => po.id !== selectedPO.id));
    setSelectedPO(null);
    alert("Goods received recorded successfully!");
  };

  // ==================== DIRECT FG FUNCTIONS ====================

  const handleAddDirectFG = () => {
    setEditingDirectFG(null);
    setDirectFGForm({
      productName: "",
      productCode: "",
      checklist: [{ name: "", passed: false, remark: "" }],
      status: "pending"
    });
    setShowDirectFGModal(true);
  };

  const handleEditDirectFG = (item) => {
    setEditingDirectFG(item);
    setDirectFGForm({
      productName: item.productName,
      productCode: item.productCode,
      checklist: item.checklist,
      status: item.status
    });
    setShowDirectFGModal(true);
  };

  const handleDeleteDirectFG = (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setDirectFGItems(directFGItems.filter(item => item.id !== id));
    }
  };

  const handleSaveDirectFG = () => {
    if (editingDirectFG) {
      setDirectFGItems(directFGItems.map(item =>
        item.id === editingDirectFG.id
          ? { ...item, ...directFGForm }
          : item
      ));
    } else {
      setDirectFGItems([...directFGItems, {
        id: directFGItems.length + 1,
        ...directFGForm
      }]);
    }
    setShowDirectFGModal(false);
  };

  const handleAddChecklistItem = (type) => {
    if (type === 'direct') {
      setDirectFGForm(prev => ({
        ...prev,
        checklist: [...prev.checklist, { name: "", passed: false, remark: "" }]
      }));
    } else {
      setSubAssemblyForm(prev => ({
        ...prev,
        checklist: [...prev.checklist, { name: "", passed: false, remark: "" }]
      }));
    }
  };

  const handleChecklistChange = (type, index, field, value) => {
    if (type === 'direct') {
      const updatedChecklist = [...directFGForm.checklist];
      updatedChecklist[index][field] = value;
      setDirectFGForm(prev => ({ ...prev, checklist: updatedChecklist }));
    } else {
      const updatedChecklist = [...subAssemblyForm.checklist];
      updatedChecklist[index][field] = value;
      setSubAssemblyForm(prev => ({ ...prev, checklist: updatedChecklist }));
    }
  };

  const handleRemoveChecklistItem = (type, index) => {
    if (type === 'direct') {
      setDirectFGForm(prev => ({
        ...prev,
        checklist: prev.checklist.filter((_, i) => i !== index)
      }));
    } else {
      setSubAssemblyForm(prev => ({
        ...prev,
        checklist: prev.checklist.filter((_, i) => i !== index)
      }));
    }
  };

  // ==================== SUB ASSEMBLY FUNCTIONS ====================

  const handleAddSubAssembly = () => {
    setEditingSubAssembly(null);
    setSubAssemblyForm({
      assemblyName: "",
      assemblyCode: "",
      checklist: [{ name: "", passed: false, remark: "" }],
      status: "pending"
    });
    setShowSubAssemblyModal(true);
  };

  const handleEditSubAssembly = (item) => {
    setEditingSubAssembly(item);
    setSubAssemblyForm({
      assemblyName: item.assemblyName,
      assemblyCode: item.assemblyCode,
      checklist: item.checklist,
      status: item.status
    });
    setShowSubAssemblyModal(true);
  };

  const handleDeleteSubAssembly = (id) => {
    if (window.confirm("Are you sure you want to delete this assembly?")) {
      setSubAssemblyItems(subAssemblyItems.filter(item => item.id !== id));
    }
  };

  const handleSaveSubAssembly = () => {
    if (editingSubAssembly) {
      setSubAssemblyItems(subAssemblyItems.map(item =>
        item.id === editingSubAssembly.id
          ? { ...item, ...subAssemblyForm }
          : item
      ));
    } else {
      setSubAssemblyItems([...subAssemblyItems, {
        id: subAssemblyItems.length + 1,
        ...subAssemblyForm
      }]);
    }
    setShowSubAssemblyModal(false);
  };

  // ==================== INSPECTION FUNCTIONS ====================

  const handleAddInspection = () => {
    setEditingInspection(null);
    setInspectionForm({
      type: "direct_fg",
      itemId: "",
      inspectionDate: new Date().toISOString().split('T')[0],
      inspector: user?.name || "",
      result: "pass",
      remarks: ""
    });
    setShowInspectionModal(true);
  };

  const handleEditInspection = (inspection) => {
    setEditingInspection(inspection);
    setInspectionForm({
      type: inspection.type,
      itemId: inspection.itemId,
      inspectionDate: inspection.inspectionDate,
      inspector: inspection.inspector,
      result: inspection.result,
      remarks: inspection.remarks
    });
    setShowInspectionModal(true);
  };

  const handleDeleteInspection = (id) => {
    if (window.confirm("Are you sure you want to delete this inspection record?")) {
      setInspections(inspections.filter(inspection => inspection.id !== id));
    }
  };

  const handleSaveInspection = () => {
    const selectedItem = inspectionForm.type === 'direct_fg'
      ? directFGItems.find(i => i.id === parseInt(inspectionForm.itemId))
      : subAssemblyItems.find(i => i.id === parseInt(inspectionForm.itemId));

    if (editingInspection) {
      setInspections(inspections.map(inspection =>
        inspection.id === editingInspection.id
          ? { ...inspection, ...inspectionForm, itemName: selectedItem?.productName || selectedItem?.assemblyName }
          : inspection
      ));
    } else {
      setInspections([...inspections, {
        id: inspections.length + 1,
        ...inspectionForm,
        itemName: selectedItem?.productName || selectedItem?.assemblyName,
        itemId: parseInt(inspectionForm.itemId)
      }]);
    }
    setShowInspectionModal(false);
  };

  // ==================== SALES RETURN FUNCTIONS ====================

  const handleAddSalesReturn = () => {
    setEditingSalesReturn(null);
    setSalesReturnForm({
      sno: salesReturns.length + 1,
      customer: "",
      product: "",
      productCode: "",
      quantity: "",
      invoiceNo: "",
      dcNo: "",
      ewayNo: "",
      status: "pending",
      addToSchedule: false
    });
    setShowSalesReturnModal(true);
  };

  const handleEditSalesReturn = (item) => {
    setEditingSalesReturn(item);
    setSalesReturnForm(item);
    setShowSalesReturnModal(true);
  };

  const handleDeleteSalesReturn = (id) => {
    if (window.confirm("Are you sure you want to delete this sales return record?")) {
      setSalesReturns(salesReturns.filter(item => item.id !== id));
    }
  };

  const handleSaveSalesReturn = () => {
    if (editingSalesReturn) {
      setSalesReturns(salesReturns.map(item =>
        item.id === editingSalesReturn.id
          ? { ...item, ...salesReturnForm }
          : item
      ));
    } else {
      setSalesReturns([...salesReturns, {
        id: salesReturns.length + 1,
        ...salesReturnForm
      }]);
    }
    setShowSalesReturnModal(false);
  };

  // ==================== INSPECTION REPORT FUNCTIONS ====================

  const handleGenerateReport = () => {
    let filteredInspections = [...inspections];

    if (reportFilters.startDate) {
      filteredInspections = filteredInspections.filter(i => i.inspectionDate >= reportFilters.startDate);
    }
    if (reportFilters.endDate) {
      filteredInspections = filteredInspections.filter(i => i.inspectionDate <= reportFilters.endDate);
    }
    if (reportFilters.type !== "all") {
      filteredInspections = filteredInspections.filter(i => i.type === reportFilters.type);
    }
    if (reportFilters.result !== "all") {
      filteredInspections = filteredInspections.filter(i => i.result === reportFilters.result);
    }

    const report = {
      generatedDate: new Date().toLocaleString(),
      totalInspections: filteredInspections.length,
      passedCount: filteredInspections.filter(i => i.result === "pass").length,
      failedCount: filteredInspections.filter(i => i.result === "fail").length,
      pendingCount: filteredInspections.filter(i => i.result === "pending").length,
      passRate: filteredInspections.length > 0
        ? ((filteredInspections.filter(i => i.result === "pass").length / filteredInspections.length) * 100).toFixed(2)
        : 0,
      inspections: filteredInspections,
      filters: reportFilters
    };

    setInspectionReport(report);
    setShowReportModal(true);
  };

  const handlePrintReport = () => {
    window.print();
  };

  // ==================== RENDER FUNCTIONS ====================

  // Render Pending PO
  const renderPendingPO = () => (
    <div className="quality-section">
      <div className="quality-section-header">
        <h2>Pending Purchase Orders</h2>
      </div>
      <div className="quality-table-container">
        <table className="quality-table">
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Vendor</th>
              <th>Date</th>
              <th>Items</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingPOs.map(po => (
              <tr key={po.id}>
                <td>{po.poNumber}</td>
                <td>{po.vendor}</td>
                <td>{po.date}</td>
                <td>{po.items.map(i => `${i.name} (${i.qty})`).join(", ")}</td>
                <td><span className="quality-badge warning">{po.status}</span></td>
                <td>
                  <button className="quality-action-btn primary" onClick={() => handleSelectPO(po)}>
                    Receive Goods
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedPO && (
        <div className="quality-modal-overlay" onClick={() => setSelectedPO(null)}>
          <div className="quality-modal large" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>Goods Received from Outsource Vendor</h3>
              <button className="quality-modal-close" onClick={() => setSelectedPO(null)}>×</button>
            </div>
            <div className="quality-modal-body">
              <div className="quality-info-box">
                <p><strong>PO Number:</strong> {selectedPO.poNumber}</p>
                <p><strong>Vendor:</strong> {selectedPO.vendor}</p>
                <p><strong>Date:</strong> {selectedPO.date}</p>
              </div>

              <div className="quality-form-group">
                <label>Quantity Accepted</label>
                <input
                  type="number"
                  className="quality-form-input"
                  value={receivedData.quantityAccepted}
                  onChange={(e) => handleReceivedDataChange("quantityAccepted", e.target.value)}
                />
              </div>

              <div className="quality-form-group">
                <label>Quantity Rejected</label>
                <input
                  type="number"
                  className="quality-form-input"
                  value={receivedData.quantityRejected}
                  onChange={(e) => handleReceivedDataChange("quantityRejected", e.target.value)}
                />
              </div>

              <div className="quality-form-group">
                <label>Quantity NC (Non-Conforming)</label>
                <div className="quality-nc-section">
                  <div className="quality-form-row">
                    <input
                      type="number"
                      placeholder="Return"
                      className="quality-form-input"
                      value={receivedData.quantityNC.return}
                      onChange={(e) => handleNCChange("return", null, e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Scrap"
                      className="quality-form-input"
                      value={receivedData.quantityNC.scrap}
                      onChange={(e) => handleNCChange("scrap", null, e.target.value)}
                    />
                  </div>
                  <div className="quality-form-group">
                    <label>Rework</label>
                    <div className="quality-form-row">
                      <input
                        type="number"
                        placeholder="Inhouse"
                        className="quality-form-input"
                        value={receivedData.quantityNC.rework.inhouse}
                        onChange={(e) => handleNCChange("rework", "inhouse", e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Outsource"
                        className="quality-form-input"
                        value={receivedData.quantityNC.rework.outsource}
                        onChange={(e) => handleNCChange("rework", "outsource", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button className="quality-submit-btn" onClick={handleSubmitGoodsReceived}>
                <Send size={18} /> Submit Received Goods
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Direct FG
  const renderDirectFG = () => (
    <div className="quality-section">
      <div className="quality-section-header">
        <h2>Direct Products (Direct to Finished Goods)</h2>
        <button className="quality-add-btn" onClick={handleAddDirectFG}>
          <Plus size={18} /> Add Product
        </button>
      </div>

      <div className="quality-filters">
        <div className="quality-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="quality-search-input"
          />
        </div>
        <select className="quality-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <div className="quality-cards-grid">
        {directFGItems
          .filter(item => item.productName.toLowerCase().includes(searchTerm.toLowerCase()))
          .filter(item => statusFilter === "all" || item.status === statusFilter)
          .map(item => (
            <div key={item.id} className="quality-card">
              <div className="quality-card-header">
                <h3>{item.productName}</h3>
                <span className={`quality-status-badge ${item.status}`}>{item.status}</span>
              </div>
              <p className="quality-card-code">Code: {item.productCode}</p>
              <div className="quality-checklist-preview">
                <strong>Checklist:</strong>
                <ul>
                  {item.checklist.map((check, idx) => (
                    <li key={idx}>
                      {check.name}: {check.passed ? "✓ Pass" : "✗ Fail"}
                      {check.remark && <span className="quality-remark"> - {check.remark}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="quality-card-actions">
                <button className="quality-action-btn edit" onClick={() => handleEditDirectFG(item)}>
                  <Edit size={16} /> Edit
                </button>
                <button className="quality-action-btn delete" onClick={() => handleDeleteDirectFG(item.id)}>
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Direct FG Modal */}
      {showDirectFGModal && (
        <div className="quality-modal-overlay" onClick={() => setShowDirectFGModal(false)}>
          <div className="quality-modal large" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>{editingDirectFG ? "Edit Product" : "Add New Product"}</h3>
              <button className="quality-modal-close" onClick={() => setShowDirectFGModal(false)}>×</button>
            </div>
            <div className="quality-modal-body">
              <div className="quality-form-group">
                <label>Product Name</label>
                <input
                  type="text"
                  className="quality-form-input"
                  value={directFGForm.productName}
                  onChange={(e) => setDirectFGForm(prev => ({ ...prev, productName: e.target.value }))}
                />
              </div>
              <div className="quality-form-group">
                <label>Product Code</label>
                <input
                  type="text"
                  className="quality-form-input"
                  value={directFGForm.productCode}
                  onChange={(e) => setDirectFGForm(prev => ({ ...prev, productCode: e.target.value }))}
                />
              </div>
              <div className="quality-form-group">
                <label>Status</label>
                <select
                  className="quality-form-select"
                  value={directFGForm.status}
                  onChange={(e) => setDirectFGForm(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="quality-form-group">
                <label>Checklist</label>
                {directFGForm.checklist.map((check, index) => (
                  <div key={index} className="quality-checklist-item">
                    <input
                      type="text"
                      placeholder="Check item name"
                      className="quality-form-input"
                      value={check.name}
                      onChange={(e) => handleChecklistChange('direct', index, 'name', e.target.value)}
                    />
                    <select
                      className="quality-form-select"
                      value={check.passed}
                      onChange={(e) => handleChecklistChange('direct', index, 'passed', e.target.value === 'true')}
                    >
                      <option value="true">Pass</option>
                      <option value="false">Fail</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Remark (optional)"
                      className="quality-form-input"
                      value={check.remark}
                      onChange={(e) => handleChecklistChange('direct', index, 'remark', e.target.value)}
                    />
                    <button
                      type="button"
                      className="quality-icon-btn danger"
                      onClick={() => handleRemoveChecklistItem('direct', index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="quality-add-checklist-btn"
                  onClick={() => handleAddChecklistItem('direct')}
                >
                  <Plus size={16} /> Add Checklist Item
                </button>
              </div>
              <div className="quality-modal-footer">
                <button className="quality-cancel-btn" onClick={() => setShowDirectFGModal(false)}>Cancel</button>
                <button className="quality-save-btn" onClick={handleSaveDirectFG}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Sub Assembly
  const renderSubAssembly = () => (
    <div className="quality-section">
      <div className="quality-section-header">
        <h2>Sub Assembly Products</h2>
        <button className="quality-add-btn" onClick={handleAddSubAssembly}>
          <Plus size={18} /> Add Assembly
        </button>
      </div>

      <div className="quality-filters">
        <div className="quality-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search assemblies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="quality-search-input"
          />
        </div>
        <select className="quality-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <div className="quality-cards-grid">
        {subAssemblyItems
          .filter(item => item.assemblyName.toLowerCase().includes(searchTerm.toLowerCase()))
          .filter(item => statusFilter === "all" || item.status === statusFilter)
          .map(item => (
            <div key={item.id} className="quality-card">
              <div className="quality-card-header">
                <h3>{item.assemblyName}</h3>
                <span className={`quality-status-badge ${item.status}`}>{item.status}</span>
              </div>
              <p className="quality-card-code">Code: {item.assemblyCode}</p>
              <div className="quality-checklist-preview">
                <strong>Checklist:</strong>
                <ul>
                  {item.checklist.map((check, idx) => (
                    <li key={idx}>
                      {check.name}: {check.passed ? "✓ Pass" : "✗ Fail"}
                      {check.remark && <span className="quality-remark"> - {check.remark}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="quality-card-actions">
                <button className="quality-action-btn edit" onClick={() => handleEditSubAssembly(item)}>
                  <Edit size={16} /> Edit
                </button>
                <button className="quality-action-btn delete" onClick={() => handleDeleteSubAssembly(item.id)}>
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Sub Assembly Modal */}
      {showSubAssemblyModal && (
        <div className="quality-modal-overlay" onClick={() => setShowSubAssemblyModal(false)}>
          <div className="quality-modal large" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>{editingSubAssembly ? "Edit Assembly" : "Add New Assembly"}</h3>
              <button className="quality-modal-close" onClick={() => setShowSubAssemblyModal(false)}>×</button>
            </div>
            <div className="quality-modal-body">
              <div className="quality-form-group">
                <label>Assembly Name</label>
                <input
                  type="text"
                  className="quality-form-input"
                  value={subAssemblyForm.assemblyName}
                  onChange={(e) => setSubAssemblyForm(prev => ({ ...prev, assemblyName: e.target.value }))}
                />
              </div>
              <div className="quality-form-group">
                <label>Assembly Code</label>
                <input
                  type="text"
                  className="quality-form-input"
                  value={subAssemblyForm.assemblyCode}
                  onChange={(e) => setSubAssemblyForm(prev => ({ ...prev, assemblyCode: e.target.value }))}
                />
              </div>
              <div className="quality-form-group">
                <label>Status</label>
                <select
                  className="quality-form-select"
                  value={subAssemblyForm.status}
                  onChange={(e) => setSubAssemblyForm(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="quality-form-group">
                <label>Checklist</label>
                {subAssemblyForm.checklist.map((check, index) => (
                  <div key={index} className="quality-checklist-item">
                    <input
                      type="text"
                      placeholder="Check item name"
                      className="quality-form-input"
                      value={check.name}
                      onChange={(e) => handleChecklistChange('sub', index, 'name', e.target.value)}
                    />
                    <select
                      className="quality-form-select"
                      value={check.passed}
                      onChange={(e) => handleChecklistChange('sub', index, 'passed', e.target.value === 'true')}
                    >
                      <option value="true">Pass</option>
                      <option value="false">Fail</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Remark (optional)"
                      className="quality-form-input"
                      value={check.remark}
                      onChange={(e) => handleChecklistChange('sub', index, 'remark', e.target.value)}
                    />
                    <button
                      type="button"
                      className="quality-icon-btn danger"
                      onClick={() => handleRemoveChecklistItem('sub', index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="quality-add-checklist-btn"
                  onClick={() => handleAddChecklistItem('sub')}
                >
                  <Plus size={16} /> Add Checklist Item
                </button>
              </div>
              <div className="quality-modal-footer">
                <button className="quality-cancel-btn" onClick={() => setShowSubAssemblyModal(false)}>Cancel</button>
                <button className="quality-save-btn" onClick={handleSaveSubAssembly}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Inspection List
  const renderInspectionList = () => (
    <div className="quality-section">
      <div className="quality-section-header">
        <h2>Inspection Records</h2>
        <button className="quality-add-btn" onClick={handleAddInspection}>
          <Plus size={18} /> Add Inspection
        </button>
      </div>

      <div className="quality-table-container">
        <table className="quality-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Item Name</th>
              <th>Inspection Date</th>
              <th>Inspector</th>
              <th>Result</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inspections.map(inspection => (
              <tr key={inspection.id}>
                <td>{inspection.type === 'direct_fg' ? 'Direct FG' : 'Sub Assembly'}</td>
                <td>{inspection.itemName}</td>
                <td>{inspection.inspectionDate}</td>
                <td>{inspection.inspector}</td>
                <td>
                  <span className={`quality-result-badge ${inspection.result}`}>
                    {inspection.result === 'pass' ? 'Pass' : inspection.result === 'fail' ? 'Fail' : 'Pending'}
                  </span>
                </td>
                <td>{inspection.remarks || '-'}</td>
                <td>
                  <button className="quality-action-btn edit" onClick={() => handleEditInspection(inspection)}>
                    <Edit size={16} />
                  </button>
                  <button className="quality-action-btn delete" onClick={() => handleDeleteInspection(inspection.id)}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inspection Modal */}
      {showInspectionModal && (
        <div className="quality-modal-overlay" onClick={() => setShowInspectionModal(false)}>
          <div className="quality-modal" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>{editingInspection ? "Edit Inspection" : "Add New Inspection"}</h3>
              <button className="quality-modal-close" onClick={() => setShowInspectionModal(false)}>×</button>
            </div>
            <div className="quality-modal-body">
              <div className="quality-form-group">
                <label>Inspection Type</label>
                <select
                  className="quality-form-select"
                  value={inspectionForm.type}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, type: e.target.value, itemId: "" }))}
                >
                  <option value="direct_fg">Direct Finished Goods</option>
                  <option value="sub_assembly">Sub Assembly</option>
                </select>
              </div>
              <div className="quality-form-group">
                <label>Select Item</label>
                <select
                  className="quality-form-select"
                  value={inspectionForm.itemId}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, itemId: e.target.value }))}
                >
                  <option value="">Select Item</option>
                  {inspectionForm.type === 'direct_fg'
                    ? directFGItems.map(item => (
                        <option key={item.id} value={item.id}>{item.productName} ({item.productCode})</option>
                      ))
                    : subAssemblyItems.map(item => (
                        <option key={item.id} value={item.id}>{item.assemblyName} ({item.assemblyCode})</option>
                      ))
                  }
                </select>
              </div>
              <div className="quality-form-group">
                <label>Inspection Date</label>
                <input
                  type="date"
                  className="quality-form-input"
                  value={inspectionForm.inspectionDate}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, inspectionDate: e.target.value }))}
                />
              </div>
              <div className="quality-form-group">
                <label>Inspector</label>
                <input
                  type="text"
                  className="quality-form-input"
                  value={inspectionForm.inspector}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, inspector: e.target.value }))}
                />
              </div>
              <div className="quality-form-group">
                <label>Result</label>
                <select
                  className="quality-form-select"
                  value={inspectionForm.result}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, result: e.target.value }))}
                >
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="quality-form-group">
                <label>Remarks</label>
                <textarea
                  className="quality-form-textarea"
                  rows="3"
                  value={inspectionForm.remarks}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, remarks: e.target.value }))}
                />
              </div>
              <div className="quality-modal-footer">
                <button className="quality-cancel-btn" onClick={() => setShowInspectionModal(false)}>Cancel</button>
                <button className="quality-save-btn" onClick={handleSaveInspection}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Generate Inspection Report
  const renderGenerateReport = () => (
    <div className="quality-section">
      <div className="quality-section-header">
        <h2>Generate Inspection Report</h2>
      </div>

      <div className="quality-report-filters">
        <div className="quality-form-row">
          <div className="quality-form-group">
            <label>Start Date</label>
            <input
              type="date"
              className="quality-form-input"
              value={reportFilters.startDate}
              onChange={(e) => setReportFilters(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="quality-form-group">
            <label>End Date</label>
            <input
              type="date"
              className="quality-form-input"
              value={reportFilters.endDate}
              onChange={(e) => setReportFilters(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
          <div className="quality-form-group">
            <label>Inspection Type</label>
            <select
              className="quality-form-select"
              value={reportFilters.type}
              onChange={(e) => setReportFilters(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="all">All Types</option>
              <option value="direct_fg">Direct Finished Goods</option>
              <option value="sub_assembly">Sub Assembly</option>
            </select>
          </div>
          <div className="quality-form-group">
            <label>Result</label>
            <select
              className="quality-form-select"
              value={reportFilters.result}
              onChange={(e) => setReportFilters(prev => ({ ...prev, result: e.target.value }))}
            >
              <option value="all">All Results</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
        <button className="quality-generate-btn" onClick={handleGenerateReport}>
          <BarChart3 size={18} /> Generate Report
        </button>
      </div>

      {/* Inspection Report Modal */}
      {showReportModal && inspectionReport && (
        <div className="quality-modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="quality-modal xlarge" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>Inspection Report</h3>
              <button className="quality-modal-close" onClick={() => setShowReportModal(false)}>×</button>
            </div>
            <div className="quality-modal-body">
              <div className="quality-report-header">
                <h2>Quality Inspection Report</h2>
                <p>Generated on: {inspectionReport.generatedDate}</p>
              </div>

              <div className="quality-report-stats">
                <div className="quality-stat-card">
                  <h4>Total Inspections</h4>
                  <p>{inspectionReport.totalInspections}</p>
                </div>
                <div className="quality-stat-card success">
                  <h4>Passed</h4>
                  <p>{inspectionReport.passedCount}</p>
                </div>
                <div className="quality-stat-card danger">
                  <h4>Failed</h4>
                  <p>{inspectionReport.failedCount}</p>
                </div>
                <div className="quality-stat-card warning">
                  <h4>Pending</h4>
                  <p>{inspectionReport.pendingCount}</p>
                </div>
                <div className="quality-stat-card primary">
                  <h4>Pass Rate</h4>
                  <p>{inspectionReport.passRate}%</p>
                </div>
              </div>

              <div className="quality-table-container">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Item Name</th>
                      <th>Inspection Date</th>
                      <th>Inspector</th>
                      <th>Result</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspectionReport.inspections.map(inspection => (
                      <tr key={inspection.id}>
                        <td>{inspection.type === 'direct_fg' ? 'Direct FG' : 'Sub Assembly'}</td>
                        <td>{inspection.itemName}</td>
                        <td>{inspection.inspectionDate}</td>
                        <td>{inspection.inspector}</td>
                        <td>
                          <span className={`quality-result-badge ${inspection.result}`}>
                            {inspection.result === 'pass' ? 'Pass' : inspection.result === 'fail' ? 'Fail' : 'Pending'}
                          </span>
                        </td>
                        <td>{inspection.remarks || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="quality-modal-footer">
                <button className="quality-print-btn" onClick={handlePrintReport}>
                  <Printer size={18} /> Print Report
                </button>
                <button className="quality-cancel-btn" onClick={() => setShowReportModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Sales Return
  const renderSalesReturn = () => (
    <div className="quality-section">
      <div className="quality-section-header">
        <h2>List of Sales Return Received</h2>
        <button className="quality-add-btn" onClick={handleAddSalesReturn}>
          <Plus size={18} /> Add Return Record
        </button>
      </div>

      <div className="quality-table-container">
        <table className="quality-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Product Code</th>
              <th>Quantity</th>
              <th>Invoice No</th>
              <th>DC No</th>
              <th>Eway No</th>
              <th>Status/Remark</th>
              <th>Add to Schedule</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {salesReturns.map(item => (
              <tr key={item.id}>
                <td>{item.sno}</td>
                <td>{item.customer}</td>
                <td>{item.product}</td>
                <td>{item.productCode}</td>
                <td>{item.quantity}</td>
                <td>{item.invoiceNo}</td>
                <td>{item.dcNo}</td>
                <td>{item.ewayNo}</td>
                <td><span className={`quality-badge ${item.status === 'approved' ? 'success' : 'warning'}`}>{item.status}</span></td>
                <td>{item.addToSchedule ? 'Yes' : 'No'}</td>
                <td>
                  <button className="quality-action-btn edit" onClick={() => handleEditSalesReturn(item)}>
                    <Edit size={16} />
                  </button>
                  <button className="quality-action-btn delete" onClick={() => handleDeleteSalesReturn(item.id)}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sales Return Modal */}
      {showSalesReturnModal && (
        <div className="quality-modal-overlay" onClick={() => setShowSalesReturnModal(false)}>
          <div className="quality-modal large" onClick={e => e.stopPropagation()}>
            <div className="quality-modal-header">
              <h3>{editingSalesReturn ? "Edit Sales Return" : "Add Sales Return"}</h3>
              <button className="quality-modal-close" onClick={() => setShowSalesReturnModal(false)}>×</button>
            </div>
            <div className="quality-modal-body">
              <div className="quality-form-row">
                <div className="quality-form-group">
                  <label>S.No</label>
                  <input
                    type="number"
                    className="quality-form-input"
                    value={salesReturnForm.sno}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, sno: e.target.value }))}
                  />
                </div>
                <div className="quality-form-group">
                  <label>Customer</label>
                  <input
                    type="text"
                    className="quality-form-input"
                    value={salesReturnForm.customer}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, customer: e.target.value }))}
                  />
                </div>
              </div>
              <div className="quality-form-row">
                <div className="quality-form-group">
                  <label>Product</label>
                  <input
                    type="text"
                    className="quality-form-input"
                    value={salesReturnForm.product}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, product: e.target.value }))}
                  />
                </div>
                <div className="quality-form-group">
                  <label>Product Code</label>
                  <input
                    type="text"
                    className="quality-form-input"
                    value={salesReturnForm.productCode}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, productCode: e.target.value }))}
                  />
                </div>
              </div>
              <div className="quality-form-row">
                <div className="quality-form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    className="quality-form-input"
                    value={salesReturnForm.quantity}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, quantity: e.target.value }))}
                  />
                </div>
                <div className="quality-form-group">
                  <label>Invoice No</label>
                  <input
                    type="text"
                    className="quality-form-input"
                    value={salesReturnForm.invoiceNo}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, invoiceNo: e.target.value }))}
                  />
                </div>
              </div>
              <div className="quality-form-row">
                <div className="quality-form-group">
                  <label>DC No</label>
                  <input
                    type="text"
                    className="quality-form-input"
                    value={salesReturnForm.dcNo}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, dcNo: e.target.value }))}
                  />
                </div>
                <div className="quality-form-group">
                  <label>Eway No</label>
                  <input
                    type="text"
                    className="quality-form-input"
                    value={salesReturnForm.ewayNo}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, ewayNo: e.target.value }))}
                  />
                </div>
              </div>
              <div className="quality-form-row">
                <div className="quality-form-group">
                  <label>Status/Remark</label>
                  <select
                    className="quality-form-select"
                    value={salesReturnForm.status}
                    onChange={(e) => setSalesReturnForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="quality-form-group">
                  <label className="quality-checkbox-label">
                    <input
                      type="checkbox"
                      checked={salesReturnForm.addToSchedule}
                      onChange={(e) => setSalesReturnForm(prev => ({ ...prev, addToSchedule: e.target.checked }))}
                    />
                    Add to existing schedule
                  </label>
                </div>
              </div>
              <div className="quality-modal-footer">
                <button className="quality-cancel-btn" onClick={() => setShowSalesReturnModal(false)}>Cancel</button>
                <button className="quality-save-btn" onClick={handleSaveSalesReturn}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Placeholder render for other tabs
  const renderPlaceholder = () => (
    <div className="quality-content-card">
      <h2 className="quality-page-title">{getPageTitle()}</h2>
      {/* <p className="quality-page-description">Content for {getPageTitle()} will be displayed here.</p> */}
    </div>
  );

  // Main render based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "pending-po":
        return renderPendingPO();
      case "direct-products":
        return renderDirectFG();
      case "last-process-product":
        return renderSubAssembly();
      case "generate-inspection-report":
      case "generate-inspection-report-2":
        return renderGenerateReport();
      case "list-sales-return":
        return renderSalesReturn();
      case "check-outsource-vendor":
      case "goods-received-outsource":
        return renderPendingPO();
      case "add-instruments":
      case "list-instruments":
      case "send-outsource-vendor":
      case "list-of-drawing":
      case "npd-dashboard":
      case "nc-stock":
      case "rejection-stock":
      case "rw-stock":
      default:
        return renderPlaceholder();
    }
  };

  const getPageTitle = () => {
    const tab = tabItems.find(t => t.id === activeTab);
    return tab ? tab.label : "Quality Dashboard";
  };

  const tabItems = [
    { id: "pending-po", label: "Pending PO", icon: <FileText size={18} /> },
    { id: "check-outsource-vendor", label: "Check Outsource to Vendor", icon: <Truck size={18} /> },
    { id: "add-instruments", label: "Add Instruments - Category and Sub category", icon: <Wrench size={18} /> },
    { id: "list-instruments", label: "List Instruments", icon: <Layers size={18} /> },
    { id: "direct-products", label: "Direct Products (from direct to finished)", icon: <Package size={18} /> },
    { id: "last-process-product", label: "Last Process Product (After last p. finished)", icon: <ClipboardList size={18} /> },
    { id: "add-last-process", label: "Add Last Process", icon: <FileCheck size={18} /> },
    { id: "generate-inspection-report", label: "Generate Inspection Report", icon: <BarChart3 size={18} /> },
    { id: "send-outsource-vendor", label: "Send Outsource to Vendor", icon: <Truck size={18} /> },
    { id: "generate-inspection-report-2", label: "Generate Inspection Report", icon: <BarChart3 size={18} /> },
    { id: "goods-received-outsource", label: "Goods received from outsource vendor", icon: <Package size={18} /> },
    { id: "list-of-drawing", label: "List of drawing", icon: <FileText size={18} /> },
    { id: "npd-dashboard", label: "NPD Dashboard", icon: <BarChart3 size={18} /> },
    { id: "list-sales-return", label: "List of sales return received", icon: <Undo2 size={18} /> },
    { id: "nc-stock", label: "NC Stock", icon: <AlertTriangle size={18} /> },
    { id: "rejection-stock", label: "Rejection Stock", icon: <XCircle size={18} /> },
    { id: "rw-stock", label: "RW Stock", icon: <Database size={18} /> }
  ];

  return (
    <div className="quality-dashboard-container">
      {/* Sidebar */}
      <div className="quality-sidebar">
        <div className="quality-logo-container">
          <div className="quality-logo-icon">
            <img 
              src="/rahulerp.jpg" 
              alt="Company Logo" 
              className="quality-logo-image"
            />
          </div>
          <div>
            <h2 className="quality-company-name">Ragul Auto Tech</h2>
            <p className="quality-company-tagline">AI Powered ERP</p>
          </div>
        </div>

        <nav className="quality-nav-menu">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              className={`quality-nav-item ${activeTab === tab.id ? 'quality-nav-item-active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
            >
              <span className="quality-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="quality-logout-container">
          <button onClick={handleLogoutClick} className="quality-logout-button">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="quality-main-content">
        <header className="quality-header">
          <div>
            <h1 className="quality-main-title">Quality Management Dashboard</h1>
            <div className="quality-subtitle">
              Welcome, {user?.role || 'Quality Manager'}
            </div>
          </div>
          <div className="quality-header-actions">
            <div className="quality-last-updated">Last updated: {new Date().toLocaleString()}</div>
          </div>
        </header>
        <BackButton />

        {renderContent()}

        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Quality Management</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}